import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { themeConfigs, useTheme } from '../utils/theme';
import type { ThemeVariant, ThemeConfig } from '../utils/theme';
import { useAuthV2 } from './auth-v2/AuthContext';

interface ThemeContextType {
  theme: ThemeVariant;
  themeConfig: ThemeConfig;
  setTheme: (theme: ThemeVariant) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user } = useAuthV2();
  const [theme, setTheme] = useState<ThemeVariant>(() => {
    // Try to get theme from localStorage, or set based on user role
    const savedTheme = localStorage.getItem('theme') as ThemeVariant;
    if (savedTheme && Object.keys(themeConfigs).includes(savedTheme)) {
      return savedTheme;
    }
    
    // Default to role-based theme if no saved theme
    if (user?.role === 'charity') {
      return 'charity';
    }
    return 'business';
  });

  // Get the theme configuration
  const themeConfig = useTheme(theme);

  // Update theme when user role changes
  useEffect(() => {
    if (user?.role === 'charity' && theme !== 'charity') {
      setTheme('charity');
    } else if (user?.role === 'business' && theme !== 'business') {
      setTheme('business');
    }
  }, [user, theme]);

  // Save theme to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('theme', theme);
    
    // Apply theme CSS variables to document
    const root = document.documentElement;
    root.style.setProperty('--color-primary', themeConfig.primary);
    root.style.setProperty('--color-secondary', themeConfig.secondary);
    root.style.setProperty('--color-tertiary', themeConfig.tertiary);
    root.style.setProperty('--color-accent', themeConfig.accent);
    root.style.setProperty('--color-background', themeConfig.background);
    root.style.setProperty('--color-surface', themeConfig.surface);
    root.style.setProperty('--color-text', themeConfig.text);
    root.style.setProperty('--color-text-secondary', themeConfig.textSecondary);
    root.style.setProperty('--color-error', themeConfig.error);
    root.style.setProperty('--color-success', themeConfig.success);
    root.style.setProperty('--color-warning', themeConfig.warning);
    root.style.setProperty('--card-background', themeConfig.cardBackground);
    root.style.setProperty('--card-shadow', themeConfig.cardShadow);
    root.style.setProperty('--button-primary', themeConfig.buttonPrimary);
    root.style.setProperty('--button-secondary', themeConfig.buttonSecondary);
    root.style.setProperty('--input-background', themeConfig.inputBackground);
    root.style.setProperty('--border-radius-sm', themeConfig.borderRadius.sm);
    root.style.setProperty('--border-radius-md', themeConfig.borderRadius.md);
    root.style.setProperty('--border-radius-lg', themeConfig.borderRadius.lg);
    root.style.setProperty('--border-radius-xl', themeConfig.borderRadius.xl);
    root.style.setProperty('--border-radius-full', themeConfig.borderRadius.full);
    
    // Apply font family
    document.body.style.fontFamily = themeConfig.fontFamily;
  }, [theme, themeConfig]);

  // Toggle between charity and business themes
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'charity' ? 'business' : 'charity');
  };

  return (
    <ThemeContext.Provider value={{ theme, themeConfig, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useAppTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useAppTheme must be used within a ThemeProvider');
  }
  return context;
};
